import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Phase P.27.1 — shared shadcn AlertDialog for one-off confirmations, replacing
 * scattered `window.confirm()` calls across the app.
 *
 * Controlled via a `state` prop (or `open` boolean): pass `null`/`false` to hide,
 * or an object `{title, description, subtitle?, confirmLabel?, cancelLabel?, destructive?, onConfirm}`.
 * `subtitle` renders as a muted second line below `description` (matches the
 * QuickBookingForm block-day design). `destructive=true` swaps the confirm
 * button to a destructive-tinted style; `destructive="amber"` uses the softer
 * amber palette used for reversible actions (e.g. blocked-day override).
 */
export default function ConfirmDialog({ state, onOpenChange }) {
  const open = !!state;
  const {
    title = "Are you sure?",
    description = "",
    subtitle = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = false,
    onConfirm,
    testId = "confirm-dialog",
  } = state || {};

  const confirmClass =
    destructive === "amber"
      ? "bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500"
      : destructive
      ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
      : "";

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onOpenChange?.(false); }}>
      <AlertDialogContent
        data-testid={testId}
        className="z-[70] bg-card border border-border shadow-2xl"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {(description || subtitle) && (
            <AlertDialogDescription asChild>
              <div className="space-y-2 pt-1">
                {description && (
                  <div className="text-sm text-foreground">{description}</div>
                )}
                {subtitle && (
                  <div className="text-sm text-muted-foreground">{subtitle}</div>
                )}
              </div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel-btn`}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={confirmClass}
            data-testid={`${testId}-confirm-btn`}
            onClick={() => { onConfirm?.(); }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
